import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AutoComplete, Col, Row, Select, Tabs } from 'antd';
import { debounce } from '@material-ui/core';
import { useLocation, useHistory } from 'react-router-dom';
//
import RequestCar from './RequestCar';
import CarListing from './CarAmigoListing';
import CarAmigoLogin from './CarAmigoLogin';
import ImportedCarAmigo from './ImportedCarAmigo';
import CarAmigoRequestedCarList from './CarAmigoRequestedCarList';
import { PageHeader } from '../../../components/page-headers/page-headers';
import { Main } from '../../styled';
import { getAllBrands } from '../../../redux/carbrand/actionCreator';
import { Button } from '../../../components/buttons/buttons';
import {
  getCarAmigoCars,
  getImportedCarIds,
  requestCarAmigo,
  logoutCarAmigoUser,
} from '../../../redux/carImport/carAmigo/actionCreator';
import { getAllModel } from '../../../redux/carmodel/actionCreator';
import { getTopLevelDomain } from '../../../utils/constants';
import { importTargetPortals } from '../../../utils/caramigo';

const TabStates = {
  list: 'list',
  importedList: 'imported-list',
  requestedList: 'requested-list',
};

const CarAmigo = () => {
  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);
  const [activeKey, setActiveKey] = useState(
    searchParams.get('tab') &&
      [TabStates.list, TabStates.importedList, TabStates.requestedList].includes(searchParams.get('tab'))
      ? searchParams.get('tab')
      : TabStates.list,
  );
  const [queryState, setQueryState] = useState({ limit: 10, page: 1 });
  const [showRequestCarModal, setShowRequestCarModal] = useState(false);
  const [selectedBrandFilter, setSelectedBrandFilter] = useState(null);
  const [selectedModelFilter, setSelectedModelFilter] = useState(null);
  const [importedFilter, setImportedFilter] = useState();
  const { isLoggedIn, brands, models } = useSelector(state => {
    return {
      isLoggedIn: state.carAmigo.isLoggedIn,
      brands: state.carBrand.brands,
      models: state.carModel.models,
      allImportedCarIds: state.carModel.allImportedCarIds,
    };
  });
  const dispatch = useDispatch();

  const topLevelDomain = getTopLevelDomain();
  const importPortals = importTargetPortals(topLevelDomain);

  const handleRequestCarSubmit = async (data, resetForm) => {
    const res = await dispatch(requestCarAmigo(data));
    if (res.status === 200) {
      resetForm();
      setShowRequestCarModal(false);
      setShowRequestCarModal(false);
    }
  };

  const getCarAmigoApiData = (page, limit, brandFilter, modelFilter) => {
    let filter = `${page}?limit=${limit}`;
    if (brandFilter) {
      filter += `&brand=${brandFilter}`;
    }
    if (modelFilter) {
      filter += `&model=${modelFilter}`;
    }
    dispatch(getCarAmigoCars(filter, page, limit));
  };

  const getCarAmigoApiDebounce = useCallback(
    debounce((page, limit, brand, model) => {
      getCarAmigoApiData(page, limit, brand, model);
    }, 1000),
    [],
  );

  const handleBrandChange = data => {
    setSelectedBrandFilter(data);
    getCarAmigoApiDebounce(queryState.page, queryState.limit, data, selectedModelFilter);
  };

  const handleModelChange = data => {
    setSelectedModelFilter(data);
    getCarAmigoApiDebounce(queryState.page, queryState.limit, selectedModelFilter, data);
  };

  const logout = () => {
    dispatch(logoutCarAmigoUser);
  };

  const handleTabClick = tabValue => {
    const params = new URLSearchParams(location.search);
    params.set('tab', tabValue);
    history.push({ search: params.toString() });
    setActiveKey(tabValue);
  };

  useEffect(() => {
    dispatch(getAllBrands('?all=true'));
    dispatch(getAllModel());
    dispatch(getImportedCarIds());
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      getCarAmigoApiData(queryState.page, queryState.limit, selectedBrandFilter, selectedModelFilter);
    }
  }, [queryState.page, queryState.limit, isLoggedIn]);

  return (
    <>
      <PageHeader
        ghost
        title="CarAmigo"
        buttons={
          isLoggedIn
            ? [
                <Button
                  key="1"
                  style={{ backgroundColor: '#155BDC', color: '#fff' }}
                  size="default"
                  onClick={() => setShowRequestCarModal(true)}
                >
                  Request Car
                </Button>,
                <Button
                  key="2"
                  style={{ backgroundColor: '#f02828', color: '#fff' }}
                  size="default"
                  onClick={() => logout()}
                >
                  Logout
                </Button>,
              ]
            : []
        }
      />
      <Main>
        {isLoggedIn ? (
          <>
            <Tabs
              activeKey={activeKey}
              onChange={key => handleTabClick(key)}
              tabBarExtraContent={
                <>
                  {activeKey === TabStates.list ? (
                    <Row gutter={12}>
                      <Col xs={12} sm={12}>
                        <AutoComplete
                          options={brands
                            ?.map(brand => ({ value: brand.brand }))
                            .filter(value =>
                              selectedBrandFilter
                                ? value.value.toLowerCase().includes(selectedBrandFilter.toLowerCase())
                                : true,
                            )}
                          style={{ width: '200px' }}
                          onChange={handleBrandChange}
                          placeholder="Car Brand"
                          value={selectedBrandFilter}
                        />
                      </Col>
                      <Col xs={12} sm={12}>
                        <AutoComplete
                          options={models
                            ?.map(model => ({ value: model.model }))
                            .filter(value =>
                              selectedModelFilter
                                ? value.value.toLowerCase().includes(selectedModelFilter.toLowerCase())
                                : true,
                            )}
                          style={{ width: '200px' }}
                          onChange={handleModelChange}
                          placeholder="Car Model"
                          value={selectedModelFilter}
                        />
                      </Col>
                    </Row>
                  ) : (
                    <></>
                  )}
                  {activeKey === TabStates.importedList ? (
                    <Select
                      placeholder="Filter Import Source"
                      dropdownStyle={{ zIndex: 998 }}
                      style={{ width: '175px' }}
                      onChange={value => setImportedFilter(value)}
                      value={importedFilter}
                      allowClear
                    >
                      <Select.Option value={importPortals.auto24Portal}>{importPortals.auto24Portal}</Select.Option>
                      {importPortals.africarPortal && (
                        <>
                          <Select.Option value={importPortals.africarPortal}>
                            {importPortals.africarPortal}
                          </Select.Option>
                          <Select.Option value="both">
                            {importPortals.auto24Portal} & {importPortals.africarPortal}
                          </Select.Option>
                        </>
                      )}
                    </Select>
                  ) : (
                    <></>
                  )}
                </>
              }
            >
              <Tabs.TabPane tab="Car Listing" key={TabStates.list}>
                <CarListing queryState={queryState} setQueryState={setQueryState} />
              </Tabs.TabPane>
              <Tabs.TabPane tab="Imported Cars" key={TabStates.importedList}>
                {/* added activeKey compare to refetch data when going to tab */}
                {activeKey === TabStates.importedList ? <ImportedCarAmigo importedFilter={importedFilter} /> : <></>}
              </Tabs.TabPane>
              <Tabs.TabPane tab="Requested Cars" key={TabStates.requestedList}>
                {/* same reason as above for checking active key */}
                {activeKey === TabStates.requestedList ? <CarAmigoRequestedCarList /> : <></>}
              </Tabs.TabPane>
            </Tabs>
          </>
        ) : (
          <CarAmigoLogin />
        )}
      </Main>
      <RequestCar
        visible={showRequestCarModal}
        handleSubmit={handleRequestCarSubmit}
        onCancel={() => setShowRequestCarModal(false)}
      />
    </>
  );
};

export default CarAmigo;
